import { c as _c } from "react/compiler-runtime";
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import Confirmation, { ConfirmationProps } from 'shared/components/confirmation-modal';
import { BASE_PATH_URL } from 'shared/constants/base-path-url';
import { useLocoTranslation } from '../hooks/use-loco-translation';
import { SKIP_CONFIRMATION_EXIT_ONCE_EVENT } from './event';
export interface ConfirmationModalBeforeExit extends Omit<ConfirmationProps, 'opened' | 'onClose' | 'onConfirm'> {
  hideModal?: boolean;
  shouldSkipOnRouteChange?: (route: string) => boolean;
}
export const ConfirmationModalBeforeExit = properties => {
  const $ = _c(40);
  const {
    t
  } = useLocoTranslation();
  let hideModal;
  let props;
  let shouldSkipOnRouteChange;
  let t0;
  if ($[0] !== properties) {
    ({
      confirmationText: t0,
      hideModal,
      shouldSkipOnRouteChange,
      ...props
    } = properties);
    $[0] = properties;
    $[1] = hideModal;
    $[2] = props;
    $[3] = shouldSkipOnRouteChange;
    $[4] = t0;
  } else {
    hideModal = $[1];
    props = $[2];
    shouldSkipOnRouteChange = $[3];
    t0 = $[4];
  }
  let t1;
  if ($[5] !== t || $[6] !== t0) {
    t1 = t0 === undefined ? t("confirmation_modal.confirm_text_before_exit") : t0;
    $[5] = t;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const confirmationText = t1;
  const router = useRouter();
  const skipOnce = useRef(false);
  const [hasConfirmed, setHasConfirmed] = useState(false);
  const [nextRoute, setNextRoute] = useState(null);
  const [isModalOpened, setIsModalOpened] = useState(false);
  let t2;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const handleDontShowModal = () => {
        skipOnce.current = true;
      };
      window.addEventListener(SKIP_CONFIRMATION_EXIT_ONCE_EVENT, handleDontShowModal);
      return () => window.removeEventListener(SKIP_CONFIRMATION_EXIT_ONCE_EVENT, handleDontShowModal);
    };
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== confirmationText || $[10] !== hasConfirmed || $[11] !== hideModal || $[12] !== t) {
    t3 = [t, hasConfirmed, confirmationText, hideModal];
    $[9] = confirmationText;
    $[10] = hasConfirmed;
    $[11] = hideModal;
    $[12] = t;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  useEffect(t2, t3);
  let t4;
  if ($[14] !== confirmationText || $[15] !== hasConfirmed || $[16] !== hideModal) {
    t4 = () => {
      if (!hideModal) {
        const handleWindowClose = e => {
          if (!skipOnce.current) {
            if (!hasConfirmed) {
              e.preventDefault();
              e.returnValue = confirmationText;
              return e.returnValue;
            }
          } else {
            skipOnce.current = false;
          }
        };
        window.addEventListener("beforeunload", handleWindowClose);
        return () => window.removeEventListener("beforeunload", handleWindowClose);
      }
    };
    $[14] = confirmationText;
    $[15] = hasConfirmed;
    $[16] = hideModal;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== confirmationText || $[19] !== hasConfirmed || $[20] !== hideModal || $[21] !== t) {
    t5 = [t, hasConfirmed, confirmationText, hideModal];
    $[18] = confirmationText;
    $[19] = hasConfirmed;
    $[20] = hideModal;
    $[21] = t;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  useEffect(t4, t5);
  let t6;
  if ($[23] !== hasConfirmed || $[24] !== hideModal || $[25] !== nextRoute || $[26] !== router || $[27] !== shouldSkipOnRouteChange) {
    t6 = () => {
      if (!hideModal) {
        if (!skipOnce.current && hasConfirmed && nextRoute) {
          router.push(nextRoute);
        }
        const onRouteChangeStart = route => {
          if (shouldSkipOnRouteChange && shouldSkipOnRouteChange(route)) {
            return;
          }
          if (!skipOnce.current) {
            if (!hasConfirmed) {
              const normalizedRoute = route === BASE_PATH_URL ? "/" : route.replace(BASE_PATH_URL, "");
              setNextRoute(normalizedRoute);
              setIsModalOpened(true);
              router.events.emit("routeChangeError");
              throw "navigation aborted by user";
            }
          } else {
            skipOnce.current = false;
          }
        };
        router.events.on("routeChangeStart", onRouteChangeStart);
        return () => router.events.off("routeChangeStart", onRouteChangeStart);
      }
    };
    $[23] = hasConfirmed;
    $[24] = hideModal;
    $[25] = nextRoute;
    $[26] = router;
    $[27] = shouldSkipOnRouteChange;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== hasConfirmed || $[30] !== hideModal || $[31] !== nextRoute || $[32] !== router) {
    t7 = [nextRoute, hasConfirmed, router, hideModal];
    $[29] = hasConfirmed;
    $[30] = hideModal;
    $[31] = nextRoute;
    $[32] = router;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setHasConfirmed(false);
      setIsModalOpened(false);
      setNextRoute(null);
    };
    t9 = async () => {
      setIsModalOpened(false);
      setHasConfirmed(true);
      await new Promise(_temp);
    };
    $[34] = t8;
    $[35] = t9;
  } else {
    t8 = $[34];
    t9 = $[35];
  }
  let t10;
  if ($[36] !== confirmationText || $[37] !== isModalOpened || $[38] !== props) {
    t10 = <Confirmation opened={isModalOpened} onClose={t8} onConfirm={t9} confirmationText={confirmationText} {...props} />;
    $[36] = confirmationText;
    $[37] = isModalOpened;
    $[38] = props;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  return t10;
};
function _temp(resolve) {
  return resolve;
}